import type { GQL_ID } from '@@partner:types:graphql'
import type { CrudFormProps } from '@@partner:types:app'
import type { PromocodeFormState } from './PromocodeFormState'

import React, { useState, useCallback, useEffect } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { useFetchSinglePromocode } from '@@partner:graphql/query/promocode/singlePromocode.gql'
import { useMutateSavePromocode } from '@@partner:graphql/mutation/promocode/savePromocode.gql'

import {
  notifyOk,
  makeGraphQLErrorNotification,
  notifyError
} from '../../../helpers/notification'
import {
  makePromocodeFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './PromocodeFormState'

import { PromocodeFormValidationModel } from './validation/PromocodeFormValidationModel'

import {
  Checkbox,
  Form,
  InputGroup,
  Panel,
  Toggle,
  Grid,
  Row,
  Col,
  DatePicker,
  Tag
} from 'rsuite'
import { SaveButton } from '@components/common/form/Buttons'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'

export interface PromocodeFormProps extends CrudFormProps {
  promocodeId?: GQL_ID
}

export default function PromocodeForm ({
  promocodeId,
  onSaved
}: PromocodeFormProps) {
  const [ formError, setFormError ] = useState({})
  const { formState, setFormState, setFormRef } =
    useValidatableForm<PromocodeFormState>(makePromocodeFormState)

  const {
    data,
    loading: loadingPromocode,
    error: errorLoadingPromocode
  } = useFetchSinglePromocode({
    id: promocodeId
  })

  const [ savePromocode, { loading: loadingSavePromocode } ] =
    useMutateSavePromocode()

  useEffect(() => {
    if (!loadingPromocode && data) {
      setFormState((state) =>
        mergeLeft(state, mapApiDataToState(data?.promocode))
      )
    }
  }, [ data, loadingPromocode, setFormState ])

  const handleFormError = useCallback((error: any) => {
    setFormError(error)
  }, [])

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        savePromocode({
          variables: {
            promocodeInfo: mapStateToApiVariable(formState)
          }
        })
          .then(({ data }) => {
            notifyOk('Промокод сохранён')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.promocode)
            )

            onSaved?.(data?.promocode.id)
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ savePromocode, formState, setFormState, onSaved ]
  )

  if (errorLoadingPromocode) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить промокод #${promocodeId} для редактирования`}
        error={errorLoadingPromocode}
      />
    )
  }

  return (
    <Panel>
      <Form
        model={PromocodeFormValidationModel}
        formValue={formState}
        onChange={handleFormChange}
        onSubmit={handleFormSubmit}
        onCheck={handleFormError}
        ref={setFormRef}
        fluid
      >
        <Panel className="mb-4" header="Основная информация">
          <Grid fluid>
            <Row>
              <Col md={8}>
                <Form.Group>
                  <Form.ControlLabel>Код промокода</Form.ControlLabel>
                  <div className="AddPromoCode__Controls">
                    <Form.Control placeholder="Код промокода" name="code" />
                    <p className="AddPromoCode__Controls__Description">
                      Длина промокода не более 20 символов.
                    </p>
                  </div>
                </Form.Group>
              </Col>
              <Col
                md={6}
                mdPush={10}
                className="text-center border rounded-md py-4 px-2 text-lg"
              >
                <Form.Group>
                  <Form.ControlLabel>Количество активаций</Form.ControlLabel>
                  <Tag color="green">{formState.usesCount}</Tag>
                </Form.Group>
              </Col>
            </Row>
          </Grid>
        </Panel>

        <Panel className="mb-4" header="Включение/выключение">
          <Grid fluid>
            <Row className="mt-6" gutter={24}>
              <Col md={4}>
                <Form.Group>
                  <Form.ControlLabel>Активен</Form.ControlLabel>

                  <Form.Control
                    accepter={Toggle}
                    size="lg"
                    checked={formState.enabled}
                    checkedChildren="ВКЛЮЧЕНО"
                    unCheckedChildren="ВЫКЛЮЧЕНО"
                    name="enabled"
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.ControlLabel>Период промоакции</Form.ControlLabel>

                <InputGroup>
                  <Form.Control
                    accepter={DatePicker}
                    block
                    oneTap
                    isoWeek
                    appearance="subtle"
                    name="activeFrom"
                    value={formState.activeFrom}
                    placeholder="дата начала действия"
                  />

                  <InputGroup.Addon> до </InputGroup.Addon>

                  <Form.Control
                    accepter={DatePicker}
                    block
                    oneTap
                    isoWeek
                    appearance="subtle"
                    name="activeUntil"
                    value={formState.activeUntil}
                    placeholder="дата окончания действия"
                  />
                </InputGroup>
              </Col>
            </Row>
          </Grid>
        </Panel>

        <Panel className="mb-4" header="Настройки скидок">
          <Grid fluid>
            <Row gutter={24} className="mt-6">
              <Col md={8}>
                <Form.Group>
                  <Form.ControlLabel>Скидка на доставку</Form.ControlLabel>
                  <InputGroup inside className="flex items-center">
                    <Form.Control
                      placeholder="Скидка на доставку"
                      name="deliveryDiscount"
                    />
                    <InputGroup.Addon>
                      <Form.Control
                        accepter={Toggle}
                        size="md"
                        checked={formState.deliveryDiscountInPercent}
                        checkedChildren="Процент"
                        unCheckedChildren="Валюта"
                        name="deliveryDiscountInPercent"
                      />
                    </InputGroup.Addon>
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={8}>
                <Form.Group>
                  <Form.ControlLabel>Скидка на товары</Form.ControlLabel>

                  <InputGroup inside className="flex items-center">
                    <Form.Control
                      placeholder="Скидка на товары"
                      name="cartDiscount"
                    />
                    <InputGroup.Addon>
                      <Form.Control
                        accepter={Toggle}
                        size="md"
                        checked={formState.cartDiscountInPercent}
                        checkedChildren="Процент"
                        unCheckedChildren="Валюта"
                        name="cartDiscountInPercent"
                      />
                    </InputGroup.Addon>
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={8}>
                <Form.Group>
                  <Form.ControlLabel>Максимальная скидка</Form.ControlLabel>

                  <InputGroup inside className="flex items-center">
                    <Form.Control
                      placeholder="Сумма скидки"
                      name="maxOrderDiscount"
                    />
                    <InputGroup.Addon>
                      <Form.Control
                        accepter={Toggle}
                        size="md"
                        checked={formState.maxOrderDiscountInPercent}
                        checkedChildren="Процент"
                        unCheckedChildren="Валюта"
                        name="maxOrderDiscountInPercent"
                      />
                    </InputGroup.Addon>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </Grid>
        </Panel>

        <Panel className="mb-4" header="Прочие настройки">
          <Grid fluid>
            <Row className="mt-6" gutter={24}>
              <Col md={4}>
                <Form.Group>
                  <Form.ControlLabel>
                    Только для первой доставки
                  </Form.ControlLabel>
                  <Checkbox
                    name="firstDelivery"
                    checked={formState.firstDelivery}
                    onChange={(_, checked) => setFormState((state) => ({ ...state, firstDelivery: checked }))}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.ControlLabel>Минимальная сумма корзины</Form.ControlLabel>
                  <Form.Control
                    placeholder="Минимальная сумма корзины"
                    name="minCartAmount"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.ControlLabel>
                    Максимальное количество активаций
                  </Form.ControlLabel>

                  <Form.Control
                    placeholder="Максимальное количество активаций"
                    name="activationLimit"
                    disabled={formState.noActivationLimit}
                  />
                </Form.Group>
                <Form.Group>
                  <Checkbox
                    name="noActivationLimit"
                    checked={formState.noActivationLimit}
                    onChange={(_, checked) => setFormState((state) => ({ ...state, noActivationLimit: checked }))}
                  >
                    Без ограничений
                  </Checkbox>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.ControlLabel>
                    Максимальное количество активаций одним пользователем
                  </Form.ControlLabel>

                  <Form.Control
                    placeholder="Максимальное количество активаций одним пользователем"
                    name="userActivationLimit"
                    disabled={formState.noUserActivationLimit}
                  />
                </Form.Group>
                <Form.Group>
                  <Checkbox
                    name="noUserActivationLimit"
                    checked={formState.noUserActivationLimit}
                    onChange={(_, checked) => setFormState((state) => ({ ...state, noUserActivationLimit: checked }))}
                  >
                    Без ограничений
                  </Checkbox>
                </Form.Group>
              </Col>
            </Row>
          </Grid>
        </Panel>

        <SaveButton
          loading={loadingPromocode || loadingSavePromocode}
          disabled={loadingPromocode || loadingSavePromocode}
        >
          Сохранить
        </SaveButton>
      </Form>
    </Panel>
  )
}
